import { ExtendedQueryType } from "@whatslly/types/ExtendedQueryType";
import { QueryResponseType } from "@whatslly/types/QueryResponse";
import { SelectedContactType } from "@whatslly/types/SelectedContactType";
import { UserType } from "@whatslly/types/User";
import { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { RefreshUserResponseType } from "@whatslly/types/RefreshUserResponse";
import { RefreshUserBodyType } from "@whatslly/types/RefreshUserBody";
import { HttpRequestCmdMessageType } from "@whatslly/types/HttpRequestCmdMessageType";
import { GetRedirectUrlQueryType } from "@whatslly/types/GetRedirectUrlQuery";
import { GetAppsRedirectUrlQueryType } from "@whatslly/types/integrations/auth/req/GetAppsRedirectUrlQuery";
import { ActiveActions } from "@whatslly/types/integrations/appsActions/AppsActions";
import { GetZoomMeetingsResponseType } from "@whatslly/types/integrations/apps/zoom/res/GetZoomMeetingsResponse";
import { OmittedEventParamsType } from "@whatslly/types/analytics-producer/EventParams";
import { StorageCollection, StorageCollectionValue, StorageKey, StorageTypes, StorageValue } from "./storage.constants";
import type { WaNotificationSoundPickErrorCode } from "./waNotificationSoundErrors";
import { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { WhatsappGetCssResponseType } from "@whatslly/types/integrations/im/WhatsappGetCssResponse";
import { TelegramGetCssResponseType } from "@whatslly/types/integrations/im/TelegramGetCssResponse";
import { UpdateGroupAuthSyncStatusResponseType, UpdateGroupAuthSyncStatusBodyType } from "@whatslly/types/UpdateGroupAuthSyncStatusBody";
import { UpdateAuthSyncStatusBodyType } from "@whatslly/types/UpdateAuthSyncStatusBody";
import { UpdateAuthSyncStatusResponseType } from "@whatslly/types/UpdateAuthSyncStatusResponse";
import type { IMChannelType } from "@whatslly/instant-messaging/channelTypes";
import { IFraudCheckedMessagesSchema } from "@whatslly/server-commons/database/entities/fraud-checked-messages";
import type { MessageQueueItem } from "@whatslly/types/instant-messaging/messagesEmitter";
import { TelegramSelectorsType } from "@whatslly/types/integrations/im/TelegramGetSelectorsResponse";
import type { WsIncomingMessage } from "@whatslly/types/tuvis-agent/messages";
import { RunFraudDetectionBodyType } from "@whatslly/types/integrations/fraudDetection/RunFraudDetection";
import type {
  SelfWaNotificationSoundUploadBodyType,
  SelfWaNotificationSoundUploadResponseType,
} from "@whatslly/types/SelfWaNotificationSound";

export const BG_COMMANDS_PREFIX = "whatslly-bg";
export const BgCommands = {
  ABORT_TRANSACTION: `${BG_COMMANDS_PREFIX}:abort-transaction`,
  CHECK_IF_EXT_ACTIVE: `${BG_COMMANDS_PREFIX}:check-if-ext-active`,
  CHANGE_APP_STATUS: `${BG_COMMANDS_PREFIX}:change-app-status`,
  RECONNECT: `${BG_COMMANDS_PREFIX}:reconnect`,
  QUERY: `${BG_COMMANDS_PREFIX}:query`,
  REFRESH_USER: `${BG_COMMANDS_PREFIX}:refresh-user`,
  LOGOUT: `${BG_COMMANDS_PREFIX}:logout`,
  HTTP_REQUEST: `${BG_COMMANDS_PREFIX}:http-request`,
  SEND_TO_REDIRECT: `${BG_COMMANDS_PREFIX}:send-to-redirect`,
  SAVE_THREADS: `${BG_COMMANDS_PREFIX}:save-threads`,
  RESPONSE: `${BG_COMMANDS_PREFIX}:bg-response`,
  ERROR: `${BG_COMMANDS_PREFIX}:bg-error`,
  START_NEW_WPP_CHAT: `${BG_COMMANDS_PREFIX}:start-new-wpp-chat`,
  GET_APPS_TOKEN: `${BG_COMMANDS_PREFIX}:get-apps-token`,
  SEND_TO_APPS_REDIRECT: `${BG_COMMANDS_PREFIX}:send-to-apps-redirect`,
  GET_ACTIVE_APPS_ACTIONS: `${BG_COMMANDS_PREFIX}:get-active-apps-actions`,
  GET_USER_ZOOM_MEETINGS: `${BG_COMMANDS_PREFIX}:get-user-zoom-meetings`,
  TRK: `${BG_COMMANDS_PREFIX}:track-event`,
  GET_FORCE_DOMAINS: `${BG_COMMANDS_PREFIX}:get-force-domains`,
  SELF_ADD_PHONE: `${BG_COMMANDS_PREFIX}:self-add-phone`,
  GET_KNOWN_MESSAGES: `${BG_COMMANDS_PREFIX}:get-known-messages`,
  GET_FROM_STORAGE: `${BG_COMMANDS_PREFIX}:get-from-storage`,
  SAVE_TO_STORAGE: `${BG_COMMANDS_PREFIX}:save-to-storage`,
  REMOVE_FROM_STORAGE: `${BG_COMMANDS_PREFIX}:remove-from-storage`,
  CLEAR_STORAGE: `${BG_COMMANDS_PREFIX}:clear-storage`,
  GET_INDEXED_FROM_STORAGE: `${BG_COMMANDS_PREFIX}:get-indexed-from-storage`,
  SAVE_INDEXED_TO_STORAGE: `${BG_COMMANDS_PREFIX}:save-indexed-to-storage`,
  SHIP_LOG: `${BG_COMMANDS_PREFIX}:ship-log`,
  UPDATE_AVAILABLE: `${BG_COMMANDS_PREFIX}:update-available`,
  POPUP_CONNECT_COMPLETE: `${BG_COMMANDS_PREFIX}:full-screen-popup-connect-complete`,
  ANALYTICS_TRACK: `${BG_COMMANDS_PREFIX}:track-analytics`,
  SILENCE_NOTIFICATIONS: `${BG_COMMANDS_PREFIX}:silence-notifications`,
  BADGE_COUNT: `${BG_COMMANDS_PREFIX}:badge-count`,
  GET_WHATSAPP_SELECTORS: `${BG_COMMANDS_PREFIX}:get-whatsapp-selectors`,
  GET_TELEGRAM_SELECTORS: `${BG_COMMANDS_PREFIX}:get-telegram-selectors`,
  GET_WHATSAPP_CSS: `${BG_COMMANDS_PREFIX}:get-whatsapp-css`,
  GET_TELEGRAM_CSS: `${BG_COMMANDS_PREFIX}:get-telegram-css`,
  FOCUS_WHATSAPP_TAB: `${BG_COMMANDS_PREFIX}:focus-whatsapp-tab`,
  REFRESH_WHATSAPP_TABS: `${BG_COMMANDS_PREFIX}:logout-whatsapp-tabs`,
  CHANGE_SYNC_STATUS: `${BG_COMMANDS_PREFIX}:change-sync-status`,
  CHANGE_GROUP_SYNC_STATUS: `${BG_COMMANDS_PREFIX}:change-group-sync-status`,
  RUN_FRAUD_DETECTION: `${BG_COMMANDS_PREFIX}:run-fraud-detection`,
  GET_FRAUD_DETECTION_CHECKED_MESSAGES: `${BG_COMMANDS_PREFIX}:get-fraud-detection-checked-messages`,
  START_COPY_MONITOR: `${BG_COMMANDS_PREFIX}:start-copy-monitor`,
  AI_CHAT_CONNECT: `${BG_COMMANDS_PREFIX}:ai-chat-connect`,
  AI_CHAT_COMMAND: `${BG_COMMANDS_PREFIX}:ai-chat-command`,
  AI_CHAT_DISCONNECT: `${BG_COMMANDS_PREFIX}:ai-chat-disconnect`,
  REFRESH_DLP_POLICIES: `${BG_COMMANDS_PREFIX}:refresh-dlp-policies`,
  WA_NOTIFICATION_SOUND_PICK: `${BG_COMMANDS_PREFIX}:wa-notification-sound-pick`,
  WA_NOTIFICATION_SOUND_CLEAR: `${BG_COMMANDS_PREFIX}:wa-notification-sound-clear`,
  WA_NOTIFICATION_SOUND_GET_PAYLOAD: `${BG_COMMANDS_PREFIX}:wa-notification-sound-get-payload`,
  WA_NOTIFICATION_SOUND_GET_STATUS: `${BG_COMMANDS_PREFIX}:wa-notification-sound-get-status`,
  WA_NOTIFICATION_SOUND_UPLOAD: `${BG_COMMANDS_PREFIX}:wa-notification-sound-upload`,
} as const;

export type BgCommandsKeysType = keyof typeof BgCommands;

export type BgCommandTypes = {
  [BgCommands.ABORT_TRANSACTION]: never;
  [BgCommands.RESPONSE]: never;
  [BgCommands.ERROR]: never;
  [BgCommands.CHECK_IF_EXT_ACTIVE]: () => {
    active: boolean;
    loggedIn: boolean;
    extensionVersion: string;
    pod: string;
    loginProvider: LoginProviderType;
    user: UserType | undefined;
  };
  [BgCommands.CHANGE_APP_STATUS]: (req: { newStatus?: boolean }) => void;
  [BgCommands.RECONNECT]: () => void;
  [BgCommands.QUERY]: (req: SelectedContactType & ExtendedQueryType) => QueryResponseType | undefined;
  [BgCommands.REFRESH_USER]: (req: RefreshUserBodyType) => RefreshUserResponseType | undefined;
  [BgCommands.LOGOUT]: () => void;
  [BgCommands.HTTP_REQUEST]: <R>(req: HttpRequestCmdMessageType) => R;
  [BgCommands.SEND_TO_REDIRECT]: (req: GetRedirectUrlQueryType & { type: LoginProviderType }) => void;
  [BgCommands.SAVE_THREADS]: (req: { userPhone: string; threads: MessageQueueItem[] }) => { success: boolean } | undefined;
  [BgCommands.START_NEW_WPP_CHAT]: (req: { phone: string; tabUrl?: string }) => void;
  [BgCommands.GET_APPS_TOKEN]: (req: {
    code?: string | null;
    state?: string | null;
    error?: string | null;
    errorDescription?: string | null;
    timeZone?: string;
    requestId?: string;
  }) => { url?: string };
  [BgCommands.SEND_TO_APPS_REDIRECT]: (req: GetAppsRedirectUrlQueryType) => void;
  [BgCommands.GET_USER_ZOOM_MEETINGS]: () => GetZoomMeetingsResponseType | undefined;
  [BgCommands.GET_ACTIVE_APPS_ACTIONS]: () => ActiveActions | undefined;
  [BgCommands.TRK]: (req: {
    action: string;
    traits?: Record<string, string | number | boolean>;
    split?: Record<string, string | number | boolean>[];
  }) => void;
  [BgCommands.GET_FORCE_DOMAINS]: () => {
    communityDomains: { label: string; value: string }[];
    lightningDomains: { label: string; value: string }[];
    myDomains: { label: string; value: string }[];
  };
  [BgCommands.SELF_ADD_PHONE]: (req: { phone: string }) => { success: boolean; error?: string };
  [BgCommands.GET_KNOWN_MESSAGES]: (req: { personId: string }) => string[] | undefined;
  [BgCommands.GET_FROM_STORAGE]: <K extends StorageKey | StorageKey[]>(req: {
    key: K;
  }) => K extends StorageKey ? StorageValue<K> : K extends StorageKey[] ? Pick<StorageTypes, K[number]> : never;
  [BgCommands.SAVE_TO_STORAGE]: <K extends StorageKey | Partial<StorageTypes>>(req: {
    key: K;
    data: K extends StorageKey ? StorageValue<K> : never;
  }) => void;
  [BgCommands.GET_INDEXED_FROM_STORAGE]: <K extends StorageCollection>(req: { collection: K; key: string }) => StorageCollectionValue<K> | undefined;
  [BgCommands.SAVE_INDEXED_TO_STORAGE]: <K extends StorageCollection>(req: { collection: K; key: string; data: StorageCollectionValue<K> }) => void;
  [BgCommands.REMOVE_FROM_STORAGE]: (req: { key: StorageKey | StorageKey[] }) => void;
  [BgCommands.CLEAR_STORAGE]: () => void;
  [BgCommands.SHIP_LOG]: (req: any) => void;
  [BgCommands.UPDATE_AVAILABLE]: (req: { shouldInstallUpdate?: boolean }) => void;
  [BgCommands.POPUP_CONNECT_COMPLETE]: (req: {
    code?: string;
    state?: string;
    error?: string;
    extId?: string;
    tabId?: string;
    error_description?: string;
  }) => void;
  [BgCommands.ANALYTICS_TRACK]: (req: OmittedEventParamsType & { schemaContent: { phone?: string } }) => void;
  [BgCommands.SILENCE_NOTIFICATIONS]: (req: { silence: boolean }) => void;
  [BgCommands.BADGE_COUNT]: (req: { count: number; tabId: string }) => void;
  [BgCommands.GET_WHATSAPP_SELECTORS]: (req: { channelType: IMChannelType }) => WhatsappSelectorsType | undefined;
  [BgCommands.GET_TELEGRAM_SELECTORS]: (req: { channelType: IMChannelType }) => TelegramSelectorsType | undefined;
  [BgCommands.GET_WHATSAPP_CSS]: () => WhatsappGetCssResponseType | undefined;
  [BgCommands.GET_TELEGRAM_CSS]: () => TelegramGetCssResponseType | undefined;
  [BgCommands.FOCUS_WHATSAPP_TAB]: () => void;
  [BgCommands.REFRESH_WHATSAPP_TABS]: () => void;
  [BgCommands.CHANGE_SYNC_STATUS]: (data: UpdateAuthSyncStatusBodyType & { phone: string | undefined }) => UpdateAuthSyncStatusResponseType;
  [BgCommands.CHANGE_GROUP_SYNC_STATUS]: (
    data: UpdateGroupAuthSyncStatusBodyType & { phone: string | undefined }
  ) => UpdateGroupAuthSyncStatusResponseType;
  [BgCommands.RUN_FRAUD_DETECTION]: (data: RunFraudDetectionBodyType) => Promise<IFraudCheckedMessagesSchema[] | undefined>;
  [BgCommands.GET_FRAUD_DETECTION_CHECKED_MESSAGES]: (data: { phoneNumber: string }) => Promise<IFraudCheckedMessagesSchema[] | undefined>;
  [BgCommands.START_COPY_MONITOR]: (data: { blockCopy?: boolean }) => void;
  [BgCommands.AI_CHAT_CONNECT]: () => void;
  [BgCommands.AI_CHAT_COMMAND]: (data: WsIncomingMessage) => void;
  [BgCommands.AI_CHAT_DISCONNECT]: () => void;
  [BgCommands.REFRESH_DLP_POLICIES]: () => void;
  [BgCommands.WA_NOTIFICATION_SOUND_PICK]: () =>
    | { success: true }
    | { success: false; error?: WaNotificationSoundPickErrorCode };
  [BgCommands.WA_NOTIFICATION_SOUND_CLEAR]: () => { success: boolean };
  [BgCommands.WA_NOTIFICATION_SOUND_GET_PAYLOAD]: () => { base64: string; mimeType: string } | null;
  [BgCommands.WA_NOTIFICATION_SOUND_GET_STATUS]: () => { hasCustom: boolean; displayName: string | null };
  [BgCommands.WA_NOTIFICATION_SOUND_UPLOAD]: (
    req: SelfWaNotificationSoundUploadBodyType
  ) => SelfWaNotificationSoundUploadResponseType;
};
